import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileCheck2, Upload } from 'lucide-react';
import type { ComplianceItem } from '@ip/shared-types';
import { EMPLOYER_MANAGED_COMPLIANCE_TYPES } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { cn } from '@/lib/utils';
import { formatDateMedium, todayYmd } from '@/lib/hr';
import { t } from '@/i18n';
import {
  COMPLIANCE_STATUS_CHIP, complianceDaysLabel, complianceDaysTone,
  complianceStatusLabel, complianceTypeLabel,
} from './complianceMeta';

interface ComplianceCardProps {
  nip: string;
}

/**
 * "Moje dokumenty i terminy" — the employee's own compliance requirements
 * (student card / medical exam / BHP / contract end / RUD notification) with
 * status chips and the self-service upload → manager verification flow.
 * Renders nothing when the employee has no requirements (B2B, zlecenie,
 * powołanie).
 */
export function ComplianceCard({ nip }: ComplianceCardProps) {
  const { listCompliance, submitCompliance } = useHrApi();
  const [items, setItems] = useState<ComplianceItem[] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [uploadFor, setUploadFor] = useState<ComplianceItem | null>(null);
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;
    listCompliance(nip)
      .then((rows) => { if (mounted) setItems(rows.filter((r) => r.isSelf)); })
      .catch((error) => {
        if (mounted) setItems([]);
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      });
    return () => { mounted = false; };
  }, [nip, refreshKey, listCompliance]);

  const openUpload = useCallback((item: ComplianceItem) => {
    setValidUntil('');
    setUploadFor(item);
  }, []);

  const handleSubmit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!uploadFor || !file || !validUntil) return;
    setBusy(true);
    try {
      await submitCompliance(nip, uploadFor.id, file, validUntil);
      toast.success(t('hr.compliance.uploadSuccess'));
      setUploadFor(null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error(t('hr.compliance.actionFailed'), {
          description: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  if (items === null) return <Skeleton className="h-40 w-full" />;
  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileCheck2 className="text-muted-foreground size-4" />
          {t('hr.compliance.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {items.map((item) => {
            const days = complianceDaysLabel(item);
            const isContract = item.type === 'contract_expiry';
            // Reminder-only tracks: contract end is an employer decision and
            // the RUD notification is filed by HR — nothing to upload here.
            const isReminderOnly = isContract || item.type === 'rud_report';
            // Medical exam / BHP training are ordered by the employer and
            // recorded by hr/admin — the employee only sees the dates.
            const isEmployerManaged = EMPLOYER_MANAGED_COMPLIANCE_TYPES.includes(item.type);
            const canUpload = !isReminderOnly && !isEmployerManaged && item.status !== 'pending_verification';
            return (
              <li key={item.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{complianceTypeLabel(item.type)}</p>
                  <p className={cn('text-xs', complianceDaysTone(item.daysLeft))}>
                    {isContract && item.validUntil
                      ? t('hr.compliance.contractUntil', { date: formatDateMedium(item.validUntil) })
                      : days ?? ' '}
                  </p>
                  {item.status === 'rejected' && item.rejectedReason && (
                    <p className="text-muted-foreground truncate text-xs italic" title={item.rejectedReason}>
                      {t('hr.compliance.rejectedReason', { reason: item.rejectedReason })}
                    </p>
                  )}
                </div>
                <Badge variant="outline" className={cn('shrink-0 font-normal', COMPLIANCE_STATUS_CHIP[item.status])}>
                  {complianceStatusLabel(item)}
                </Badge>
                {canUpload && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0"
                    onClick={() => openUpload(item)}
                  >
                    <Upload className="size-3.5" />
                    {item.status === 'missing' ? t('hr.compliance.upload') : t('hr.compliance.uploadAgain')}
                  </Button>
                )}
                {isEmployerManaged && (
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {t('hr.compliance.employerManagedHint')}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>

      <Dialog open={!!uploadFor} onOpenChange={(open) => !open && setUploadFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('hr.compliance.uploadTitle', {
                type: uploadFor ? complianceTypeLabel(uploadFor.type) : '',
              })}
            </DialogTitle>
            <DialogDescription>{t('hr.compliance.uploadHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="compliance-file">{t('hr.compliance.uploadFile')}</Label>
              <Input
                id="compliance-file"
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="compliance-valid-until">{t('hr.compliance.uploadValidUntil')}</Label>
              <Input
                id="compliance-valid-until"
                type="date"
                min={todayYmd()}
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadFor(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={busy || !validUntil}>
              {t('hr.compliance.uploadSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
